import { useCallback, useEffect, useMemo, useState } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import JobList from "@/elements/subcomponents/JobList";
import RecentsTable from "@elements/tables/RecentsTable";
import RequestModal from "@modals/RequestModal";
import { fetchCompanyJobsTabMerged } from "@/utils/companyJobsTabApi";

const JOB_MATCH_TEXT = {
    jobDescriptions: "Enter Jobs",
    employabilityFactors: "Define Behavioral Expectations",
    algo: "Algo",
    questionnaires: "Create Questionnaire",
    reports: "View Results",
    reviewed: "Reviewed",
    scheduled: "Scheduled",
    pending: "Pending",
    jobsMissingJDs: "Jobs Missing JDs",
    jobsPendingJdApproval: "Jobs Pending JD Approval",
    missingEFs: "Missing EF Tests",
    pendingAlgo: "Pending Algo",
    missingAlgo: "Missing Algo",
    cpasSent: "CPAS Sent",
    cpasPending: "CPAS Pending",
    noPermission: "You do not have permission to view this content.",
    workflowTitle: "Job Match Workflow",
    step: "Step",
} as const;

const JOB_MATCH_ACTIVE_STEP_STORAGE_KEY = "JobMatchActiveStep";

function toRecruitDashboard(companyJobsData) {
    const jobs = Array.isArray(companyJobsData?.jobs) ? companyJobsData.jobs : [];
    return {
        jobs: jobs.map((job) => ({
            ...job,
            descCount: Array.isArray(job?.descriptions)
                ? job.descriptions.length
                : (Number(job?.descCount) || 0),
        })),
    };
}

function JobMatchRecentsDetail({ company, adminPerms, updateSpinner }) {
    const safeUpdateSpinner =
        typeof updateSpinner === "function" ? updateSpinner : () => { };

    return (
        <RecentsTable
            tabData={{
                company,
                adminPerms: Array.isArray(adminPerms) ? adminPerms : [],
                updateSpinner: safeUpdateSpinner,
            }}
        />
    );
}

function JobMatchRequestDetail({
    company,
    updateSpinner,
    adminPerms,
    openRequestModalSignal,
    lastHandledRequestModalSignal,
    onRequestModalSignalHandled,
}) {
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

    useEffect(() => {
        if (openRequestModalSignal > lastHandledRequestModalSignal) {
            setIsRequestModalOpen(true);
            if (typeof onRequestModalSignalHandled === "function") {
                onRequestModalSignalHandled(openRequestModalSignal);
            }
        }
    }, [openRequestModalSignal, lastHandledRequestModalSignal, onRequestModalSignalHandled]);

    return (
        <RequestModal
            isOpen={isRequestModalOpen}
            title="Create Questionnaire"
            onClose={() => setIsRequestModalOpen(false)}
            companyId={company?.id}
            company={company}
            updateSpinner={updateSpinner}
            adminPerms={adminPerms}
            excludeEvalComparisons={[Enums.TEST_EVAL_COMPARISON.BE_SOURCE]}
        />
    );
}

const workflowSubcomponents = {
    JobList,
    Request: JobMatchRequestDetail,
    RecentsTable: JobMatchRecentsDetail,
};

const resolveWorkflowElement = (element, fallbackId) => {
    if (typeof element === "function") return element;
    if (typeof element === "string") {
        return workflowSubcomponents[element] ?? null;
    }
    if (fallbackId === "jds" || fallbackId === "efs" || fallbackId === "algo") return JobList;
    if (fallbackId === "questionnaires") return JobMatchRequestDetail;
    if (fallbackId === "reports") return JobMatchRecentsDetail;
    return null;
};

type JobMatchWorkflowStep = {
    id: string;
    label: string;
    helpId: string;
    element: string | typeof JobMatchRecentsDetail;
    elements?: Array<{ label: string; value: number; class?: string; highlight?: string }>;
};

const defaultJobMatchWorkflow = (): JobMatchWorkflowStep[] => [
    {
        id: "jds",
        label: JOB_MATCH_TEXT.jobDescriptions,
        helpId: "jds",
        element: "JobList",
    },
    {
        id: "efs",
        label: JOB_MATCH_TEXT.employabilityFactors,
        helpId: "efs",
        element: "JobList",
    },
    {
        id: "algo",
        label: JOB_MATCH_TEXT.algo,
        helpId: "algo",
        element: "JobList",
    },
    {
        id: "questionnaires",
        label: JOB_MATCH_TEXT.questionnaires,
        helpId: "questionnaires",
        element: "Request",
    },
    {
        id: "reports",
        label: JOB_MATCH_TEXT.reports,
        helpId: "reports",
        element: JobMatchRecentsDetail,
    },
];

function classNames(...classes: Array<string | null | undefined | false>) {
    return classes.filter(Boolean).join(" ");
}

function usesLegacyJobListLayout(stepId) {
    return stepId === "jds" || stepId === "algo";
}

export default function CompanyJobMatchTab(props) {
    const { activeTab, company, updateSpinner, adminPerms } = props.tabData;
    const [selectedCard, setSelectedCard] = useState(() => {
        try {
            return window.localStorage.getItem(JOB_MATCH_ACTIVE_STEP_STORAGE_KEY) || null;
        } catch {
            return null;
        }
    });
    const [hoveredKpiHighlight, setHoveredKpiHighlight] = useState(null);
    const [hoveredKpiColorClass, setHoveredKpiColorClass] = useState(null);
    const [jobMatchData, setJobMatchData] = useState(null);
    const [recruitDashboard, setRecruitDashboard] = useState(null);
    const [companyJobs, setCompanyJobs] = useState(null);
    const [workflowSeed, setWorkflowSeed] = useState(defaultJobMatchWorkflow);
    const [requestModalLaunchCount, setRequestModalLaunchCount] = useState(0);
    const [lastHandledRequestModalSignal, setLastHandledRequestModalSignal] = useState(0);
    const isOnThisTab = activeTab && activeTab === "jobMatch";

    useEffect(() => {
        if (!selectedCard) return;
        try {
            window.localStorage.setItem(JOB_MATCH_ACTIVE_STEP_STORAGE_KEY, selectedCard);
        } catch {
            // ignore storage failures
        }
    }, [selectedCard]);

    const workflowItems = useMemo(() => workflowSeed, [workflowSeed]);

    useEffect(() => {
        if (!selectedCard) {
            setSelectedCard(workflowItems[0]?.id ?? null);
            return;
        }
        if (!workflowItems.find((item) => item.id === selectedCard)) {
            setSelectedCard(workflowItems[0]?.id ?? null);
        }
    }, [workflowItems, selectedCard]);

    useEffect(() => {
        const jobs = Array.isArray(jobMatchData?.jobs) ? jobMatchData.jobs : [];
        const missingJDs = jobs.filter((job) => !job?.hasJobDescription).length;
        const pendingJdApproval = jobs.filter((job) => job?.hasJobDescription && !job?.jobDescriptionApproved).length;
        const missingEfs = jobs.filter((job) => !job?.hasCompletedEmployabilityFactorsTest).length;
        const pendingAlgo = jobs.filter((job) => Number(job?.stateId) === Number(Enums.JOB_STATE.PendingAlgo)).length;
        const missingAlgo = jobs.filter((job) => !job?.algoGenerated).length;
        const cpasSentTotal = jobs.reduce((sum, job) => sum + Number(job?.cpasTestsSent || 0), 0);
        const cpasPendingTotal = jobs.reduce(
            (sum, job) => sum + Math.max(0, Number(job?.cpasTestsSent || 0) - Number(job?.cpasTestsReceived || 0)),
            0
        );

        setWorkflowSeed((prev) =>
            prev.map((item) => {
                if (item.id === "jds") {
                    return {
                        ...item,
                        elements: [
                            { label: JOB_MATCH_TEXT.jobsMissingJDs, value: missingJDs, class: "kpiRed", highlight: "missingJD" },
                            { label: JOB_MATCH_TEXT.jobsPendingJdApproval, value: pendingJdApproval, class: "kpiRed", highlight: "missingJD" },
                        ],
                    };
                }
                if (item.id === "efs") {
                    return {
                        ...item,
                        elements: [
                            { label: JOB_MATCH_TEXT.missingEFs, value: missingEfs, class: "kpiRed", highlight: "missingEFs" },
                        ],
                    };
                }
                if (item.id === "algo") {
                    return {
                        ...item,
                        elements: [
                            { label: JOB_MATCH_TEXT.pendingAlgo, value: pendingAlgo, class: pendingAlgo > 0 ? "kpiRed" : "kpiGreen", highlight: "missingAlgo" },
                            { label: JOB_MATCH_TEXT.missingAlgo, value: missingAlgo, class: missingAlgo > 0 ? "kpiRed" : "kpiGreen", highlight: "missingAlgo" },
                        ],
                    };
                }
                if (item.id === "questionnaires") {
                    return {
                        ...item,
                        elements: [
                            { label: JOB_MATCH_TEXT.cpasSent, value: cpasSentTotal, class: "kpiGreen" },
                            { label: JOB_MATCH_TEXT.cpasPending, value: cpasPendingTotal, class: cpasPendingTotal > 0 ? "kpiYellow" : "kpiGreen" },
                        ],
                    };
                }
                return item;
            })
        );
    }, [jobMatchData]);

    const refreshJobMatchData = useCallback(async () => {
        if (!company?.id) return null;

        const resp = await RightTimeApi.getAsync(`/Company/${company.id}/JobMatch`);
        if (resp?.ok && resp?.jsonData) {
            setJobMatchData(resp.jsonData);
            return resp.jsonData;
        }

        return null;
    }, [company?.id]);

    const refreshRecruitDashboard = useCallback(async () => {
        if (!company?.id || !isOnThisTab) return null;

        const resp = await RightTimeApi.getAsync(`/Company/${company.id}/RecruitDashboard`);
        if (resp?.ok) {
            setRecruitDashboard(resp?.jsonData ?? null);
        }

        await refreshJobMatchData();
        return resp?.jsonData ?? null;
    }, [company?.id, isOnThisTab, refreshJobMatchData]);

    const refreshCompanyJobs = useCallback(async () => {
        if (!company?.id || !isOnThisTab) return null;

        if (typeof updateSpinner === "function") {
            updateSpinner(true);
        }

        const resp = await fetchCompanyJobsTabMerged(company.id);

        if (typeof updateSpinner === "function") {
            updateSpinner(false);
        }

        if (resp?.jsonData) {
            setCompanyJobs(resp.jsonData);
            if (!recruitDashboard) {
                setRecruitDashboard(toRecruitDashboard(resp.jsonData));
            }
            await refreshJobMatchData();
            return resp.jsonData;
        }

        return null;
    }, [company?.id, isOnThisTab, recruitDashboard, refreshJobMatchData, updateSpinner]);

    useEffect(() => {
        let isMounted = true;

        async function loadData() {
            if (!company?.id || !isOnThisTab) return;

            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }

            const [jobMatchResp, dashboardResp, jobsResp] = await Promise.all([
                RightTimeApi.getAsync(`/Company/${company.id}/JobMatch`),
                RightTimeApi.getAsync(`/Company/${company.id}/RecruitDashboard`),
                fetchCompanyJobsTabMerged(company.id),
            ]);

            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }

            if (!isMounted) return;

            if (jobMatchResp?.ok && jobMatchResp?.jsonData) {
                setJobMatchData(jobMatchResp.jsonData);
            }

            if (dashboardResp?.ok) {
                setRecruitDashboard(dashboardResp?.jsonData ?? null);
            } else if (jobsResp?.ok && jobsResp?.jsonData) {
                setRecruitDashboard(toRecruitDashboard(jobsResp.jsonData));
            }

            if (jobsResp?.ok) {
                setCompanyJobs(jobsResp.jsonData ?? null);
            }
        }

        loadData();

        return () => {
            isMounted = false;
        };
    }, [company?.id, isOnThisTab, updateSpinner]);

    if (!isOnThisTab) return null;

    const hasPermission =
        company?.permissions?.some(
            (p) =>
                p.companyId === company.id &&
                p.permissionId === Enums.ROLE_PERMS.CompanyMenuJobMatch
        ) ||
        company?.permissions?.some(
            (p) =>
                p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin
        );

    if (!hasPermission) {
        return (
            <div className="companyRecruitTab">
                <div className="tableWrapper factTable">
                    <div className="noData">{JOB_MATCH_TEXT.noPermission}</div>
                </div>
            </div>
        );
    }

    const selectedItem =
        workflowItems.find((item) => item.id === selectedCard) || workflowItems[0];
    const DetailComponent = resolveWorkflowElement(selectedItem?.element, selectedItem?.id);
    const resolvedRecruitDashboard = recruitDashboard || toRecruitDashboard(companyJobs);

    function handleHelpHover(event) {
        const source = event.target instanceof Element ? event.target : null;
        const kpiTarget = source ? source.closest("[data-kpi-highlight]") : null;
        setHoveredKpiHighlight(kpiTarget?.getAttribute("data-kpi-highlight") || null);
        setHoveredKpiColorClass(kpiTarget?.getAttribute("data-kpi-color") || null);
    }

    function handleItemClick(item) {
        setSelectedCard(item.id);
        if (item?.id === "questionnaires") {
            setRequestModalLaunchCount((prev) => prev + 1);
        }
    }

    return (
        <div
            className="companyRecruitTab"
            onMouseOver={handleHelpHover}
            onMouseLeave={() => {
                setHoveredKpiHighlight(null);
                setHoveredKpiColorClass(null);
            }}
        >
            <div className="recruitWrapper">
                <div className="recruitLeftSpacer" aria-hidden="true"></div>
                <div className="cultureWorkflow">
                    <div className="workflowHeader">
                        <div className="workflowHeaderRow">
                            <div>
                                <div className="workflowTitle">{JOB_MATCH_TEXT.workflowTitle}</div>
                            </div>
                        </div>
                    </div>
                    <div className="workflowTimeline" role="list">
                        <div className="workflowTrack" aria-hidden="true"></div>
                        <div className="workflowItems">
                            {workflowItems.map((item, index) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    role="listitem"
                                    data-help-id={item.helpId}
                                    data-workflow-id={item.id}
                                    className={
                                        item.id === selectedCard ? "workflowNode isActive" : "workflowNode"
                                    }
                                    aria-current={item.id === selectedCard ? "step" : undefined}
                                    onClick={() => handleItemClick(item)}
                                >
                                    <div className="workflowStep">
                                        {JOB_MATCH_TEXT.step} {index + 1}
                                    </div>
                                    <div className="workflowLabel">{item.label}</div>
                                    <div className="workflowKpi">
                                        {Array.isArray(item.elements) && item.elements.length > 0
                                            ? item.elements.map((element) => (
                                                <div
                                                    key={`${item.id}-${element.label}`}
                                                    className={`kpiRow ${element.class || "kpiBlack"}`}
                                                >
                                                    <span
                                                        className={classNames("kpiLabel")}
                                                        data-kpi-color={element.class || "kpiBlack"}
                                                        data-kpi-highlight={element.highlight || undefined}
                                                    >
                                                        {element.label}:
                                                    </span>
                                                    <span
                                                        className="kpiValue"
                                                        data-kpi-color={element.class || "kpiBlack"}
                                                        data-kpi-highlight={element.highlight || undefined}
                                                    >
                                                        {element.value}
                                                    </span>
                                                </div>
                                            ))
                                            : null}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="workflowDetails">
                        {selectedItem && DetailComponent ? (
                            <DetailComponent
                                company={company}
                                recruitDashboard={resolvedRecruitDashboard}
                                companyJobs={companyJobs}
                                adminPerms={adminPerms}
                                openRequestModalSignal={requestModalLaunchCount}
                                lastHandledRequestModalSignal={lastHandledRequestModalSignal}
                                onRequestModalSignalHandled={setLastHandledRequestModalSignal}
                                updateSpinner={updateSpinner}
                                refreshCompanyJobs={refreshCompanyJobs}
                                refreshRecruitDashboard={refreshRecruitDashboard}
                                workflowItem={selectedItem}
                                legacyColumnLayout={usesLegacyJobListLayout(selectedItem.id)}
                                highlightMissingJdRows={hoveredKpiHighlight === "missingJD"}
                                highlightMissingEfsRows={hoveredKpiHighlight === "missingEFs"}
                                highlightKpiColorClass={hoveredKpiColorClass}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
